import { ComputerCaptureKingStrategy } from './ComputerCaptureKingStrategy';
import { ComputerCaptureQueenStrategy } from './ComputerCaptureQueenStrategy';
import { ComputerRandomMoveStrategy } from './ComputerRandomMoveStrategy';
import { ComputerEvadeCheckStrategy } from './ComputerEvadeCheckStrategy';
import { CaptureFirstStrategy } from './CaptureFirstStrategy';
import { MoveStrategy } from './MoveStrategy';
import { BoardState, Position } from '../model/BoardState';
import { CheckmateChecker } from "./CheckmateChecker";
import { CaptureMoveDetector } from './CaptureMoveDetector';
import logger from '../utils/Logger';

/**
 * ComputerDirector is responsible for managing the strategies used by the computer player.
 */
export class ComputerDirector {
  private readonly kingStrategy: MoveStrategy;
  private readonly alternateStrategies: MoveStrategy[];
  private readonly evadeCheckStrategy: ComputerEvadeCheckStrategy;
  private currentStrategy: MoveStrategy;
  private alternateStrategyMoveCount: number;
  private initialMovesCount: number;

  constructor() {
    // Initialize strategies
    this.alternateStrategies = [
      new ComputerCaptureQueenStrategy(),
      new ComputerRandomMoveStrategy()
    ];
    this.alternateStrategyMoveCount = 0;

    this.kingStrategy = new ComputerCaptureKingStrategy();
    this.evadeCheckStrategy = new ComputerEvadeCheckStrategy();

    // Use the random move strategy for the first x moves of the game.
    this.currentStrategy = this.alternateStrategies[1];
    this.initialMovesCount = 3;
  }

  // Find the best capture moves available on the board
  private findBestCaptureMoves(boardState: BoardState): Position[] {
    const computerPieces = boardState.pieces.filter(piece => piece.color === 'white');
    let allCaptureMoves: Position[] = [];

    // Collect all possible capture moves from all pieces
    for (const piece of computerPieces) {
      const moves = boardState.getLegalMoves(piece);
      const captureMoves = moves.filter(move => 
        CaptureMoveDetector.isCaptureMove(move, boardState, 'white')
      );
      
      // Assign the piece to each move
      captureMoves.forEach(move => { move.piece = piece; });
      allCaptureMoves = [...allCaptureMoves, ...captureMoves];
    }

    // Sort by capture value and return
    return CaptureMoveDetector.prioritizeCaptureMoves(allCaptureMoves, boardState);
  }

  // Method to get the current strategy based on the game state.
  getStrategy(boardState: BoardState): MoveStrategy {
    // Check if computer is in check - if so, that takes highest priority
    const checkmateChecker = new CheckmateChecker(boardState);
    if (checkmateChecker.isInCheck('Computer').inCheck) {
      logger.debug("** Computer is in check! Switching to Computer Evade Check Strategy **");
      return this.evadeCheckStrategy;
    }
    
    // First check for any high-value capture moves across all computer pieces
    const captureMoves = this.findBestCaptureMoves(boardState);
    if (captureMoves.length > 0) {
      // If capture moves exist, return a special strategy that will use these moves
      logger.debug(`Found ${captureMoves.length} capture moves. Using CaptureFirstStrategy.`);
      logger.debug(`Computer found capture moves: ${captureMoves.length} available`);
      return new CaptureFirstStrategy(captureMoves);
    }
    
    // If no good capture moves, try the normally selected strategy
    const selectedStrategy = this.selectInitialStrategy(boardState);
    
    // Test if the selected strategy can find any legal moves
    const legalMoves = selectedStrategy.getLegalMoves(null, boardState);
    if (legalMoves.length > 0) {
      return selectedStrategy;
    }
    
    // If no legal moves found with the selected strategy, try all other strategies
    logger.debug(`No legal moves found with ${selectedStrategy.getStrategyName()}. Trying all strategies before declaring stalemate.`);
    
    // Create a list of all available strategies for fallback
    const allStrategies: MoveStrategy[] = [
      new ComputerRandomMoveStrategy(),
      new ComputerCaptureKingStrategy(),
      new ComputerCaptureQueenStrategy(),
      new ComputerEvadeCheckStrategy()
    ];
    
    // Try each strategy until we find legal moves
    for (const backupStrategy of allStrategies) {
      // Skip if it's the same as the already tried strategy
      if (backupStrategy.getStrategyName() === selectedStrategy.getStrategyName()) {
        continue;
      }
      
      logger.debug(`Trying backup strategy: ${backupStrategy.getStrategyName()}`);
      const backupLegalMoves = backupStrategy.getLegalMoves(null, boardState);
      
      if (backupLegalMoves.length > 0) {
        logger.debug(`Found ${backupLegalMoves.length} legal moves with backup strategy: ${backupStrategy.getStrategyName()}`);
        // Update the current strategy to the successful backup strategy
        this.currentStrategy = backupStrategy;
        return backupStrategy;
      }
    }
    
    // If we've tried all strategies and none found any legal moves,
    // return the original strategy (which will result in zero moves and stalemate)
    logger.debug("No legal moves found with any strategy. Genuine stalemate situation.");
    return selectedStrategy;
  }
  
  // Helper method to select the initial strategy based on previous game logic
  private selectInitialStrategy(boardState: BoardState): MoveStrategy {
    // If the computer is in check, switch to the checkmate strategy.
    const checkmateChecker = new CheckmateChecker(boardState);
    if (checkmateChecker.isInCheck('Computer').inCheck) {
      logger.debug("** Computer is in check! Switching to Computer Evade Check Strategy **");
      return this.evadeCheckStrategy;
    }

    // Use the random move strategy for the first 3 moves, but still allow capture priority to override
    // We're keeping this behavior but the capture detection will still happen first
    if (this.initialMovesCount > 0) {
      this.initialMovesCount--;
      logger.debug(`** Using Random Move Strategy for initial moves (${this.initialMovesCount} moves left) **`);
      this.currentStrategy = new ComputerRandomMoveStrategy();
      return this.currentStrategy;
    }

    // If we are in the middle of a strategy switch, continue using the current strategy.
    if (this.alternateStrategyMoveCount > 0) {
      this.alternateStrategyMoveCount--;
      const strategyName = this.currentStrategy.getStrategyName();
      logger.debug(`** Using alternate strategy (${strategyName}) for ${this.alternateStrategyMoveCount} more moves **`);
      return this.currentStrategy;
    }

    // % chance to switch to alternate strategy.
    if (Math.random() < 0.10) {
      this.alternateStrategyMoveCount = 5; // Stay on the alternate strategy for X moves
      const randomIndex = Math.floor(Math.random() * this.alternateStrategies.length);
      this.currentStrategy = this.alternateStrategies[randomIndex];
      const strategyName = this.currentStrategy.getStrategyName();
      logger.debug(`** Switching to alternate strategy (${strategyName}) for ${this.alternateStrategyMoveCount} more moves **`);
      return this.currentStrategy;
    }

    // Default to king capture strategy (used majority of the time, based on the random chance check above).
    const strategyName = this.kingStrategy.getStrategyName();
    logger.debug(`** Using ${strategyName} **`);
    this.currentStrategy = this.kingStrategy;

    return this.currentStrategy;
  }
}

